import { useEffect, useRef, type PropsWithChildren } from "react";

import { parseJWTPayload, useToken } from "@/modules/auth";
import { lockPrivacySession } from "@/modules/mine/privacy/session";
import { usePrivacySessionStore } from "@/modules/mine/privacy/store";

export function PrivacySessionBoundary({ children }: PropsWithChildren) {
  const authToken = useToken();
  const previousAuthToken = useRef(authToken);
  const expiresAt = usePrivacySessionStore((state) => state.expiresAt);

  useEffect(() => {
    if (previousAuthToken.current !== authToken) {
      const previous = previousAuthToken.current ? parseJWTPayload(previousAuthToken.current) : null;
      const current = authToken ? parseJWTPayload(authToken) : null;
      const sameSession = typeof previous?.jti === "string" && previous.jti !== ""
        && previous.jti === current?.jti && previous.sub === current?.sub;
      if (!sameSession) lockPrivacySession({ refetch: false, closeDialogs: false });
      previousAuthToken.current = authToken;
    }
  }, [authToken]);

  useEffect(() => {
    if (!expiresAt) return;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      lockPrivacySession();
      return;
    }
    const timer = window.setTimeout(lockPrivacySession, remaining);
    return () => window.clearTimeout(timer);
  }, [expiresAt]);

  return children;
}
